import { Table } from '@tanstack/react-table'

import {
  ACTION_COLUMN_ID,
  EXPORT_VALUE_CELL_FIELD,
  SELECT_COLUMN_ID,
} from '../constants'
import { ExportItem } from './default-page-export'

export const getValidExportColumnIds = <T>({
  tableInstance,
}: {
  tableInstance: Table<T>
}): string[] => {
  const uneligibleColumnIds: string[] = [ACTION_COLUMN_ID, SELECT_COLUMN_ID]

  const tableColumnIds = tableInstance
    .getAllColumns()
    .filter((column) => !uneligibleColumnIds.includes(column.id))
    .filter((column) => !column.columnDef.meta?.excludeFromExport)
    .map((column) => column.id)

  return tableColumnIds
}

export const getDefaultCheckedExportColumns = <T>({
  tableInstance,
}: {
  tableInstance: Table<T>
}): Record<string, boolean> => {
  const tableColumnIds = getValidExportColumnIds({ tableInstance })

  return tableColumnIds.reduce((obj, columnId) => {
    obj[columnId] = true
    return obj
  }, {})
}

export const isNotNull = <T>(value: T | null): value is T => value !== null

export const escapeCSVField = (field: string): string => {
  if (field.includes(',') || field.includes('"') || field.includes('\n')) {
    return `"${field.replace(/"/g, '""')}"` // Escape quotes and enclose in quotes
  }
  return field
}

export const generateExportItem = <T>({
  tableInstance,
  enabledExportColumns,
}: {
  tableInstance: Table<T>
  enabledExportColumns: string[]
}): ExportItem => {
  const headers = enabledExportColumns.map((columnId) => {
    const column = tableInstance.getColumn(columnId)

    if (!column) {
      return null
    }

    return typeof column.columnDef.header === 'string'
      ? column.columnDef.header
      : columnId
  })

  const rows = tableInstance.getRowModel().rows.map((row) => {
    const rowValues = row
      .getVisibleCells()
      .filter((c) => enabledExportColumns.includes(c.column.id))
      .map((cell) => {
        const cellValue:
          | string
          | number
          | object
          | unknown
          | {
              [EXPORT_VALUE_CELL_FIELD]: string | number
            } = cell.getValue() as
          | string
          | number
          | object
          | unknown
          | {
              [EXPORT_VALUE_CELL_FIELD]: string
            }
        let value = ''

        if (
          typeof cellValue === 'object' &&
          cellValue !== null &&
          EXPORT_VALUE_CELL_FIELD in cellValue &&
          (typeof cellValue[EXPORT_VALUE_CELL_FIELD] === 'string' ||
            typeof cellValue[EXPORT_VALUE_CELL_FIELD] === 'number')
        ) {
          value = cellValue[EXPORT_VALUE_CELL_FIELD]?.toString() ?? ''
        } else if (typeof cellValue === 'string') {
          value = cellValue
        } else if (typeof cellValue === 'number') {
          return cellValue.toString()
        } else {
          value = ''
        }

        return escapeCSVField(value)
      })

    return rowValues
  })

  return {
    headers: headers.filter(isNotNull),
    rows,
  }
}

export const generateCsvStringFromExportItem = ({
  exportItem,
}: {
  exportItem: ExportItem
}): string => {
  const { headers, rows } = exportItem
  let csvString = headers.join(',') + '\n'

  rows.forEach((row) => {
    csvString += row.join(',') + '\n'
  })

  return csvString
}

export const generateCsvBlobFromString = ({
  csvString,
}: {
  csvString: string
}): Blob => {
  return new Blob([csvString], { type: 'text/csv;charset=utf-8;' })
}
